/* global OffscreenCanvas */
// IndexedDB helper for storing alternate paperdoll assets

const DB_NAME = 'paperdoll-alternates';
const STORE_NAME = 'alternates';
const DB_VERSION = 1;

/**
 * @typedef {object} AlternateEntry
 * @property {Blob} blob
 * @property {string} objectUrl
 * @property {Blob} previewBlob
 * @property {string} previewObjectUrl
 * @property {boolean} useAlternate
 */

/** @type {IDBDatabase|null} */
let dbInstance = null;

/** @returns {Promise<IDBDatabase>} */
function openAlternatesDB() {
  if (dbInstance) return Promise.resolve(dbInstance);
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE_NAME);
    req.onsuccess = () => {
      dbInstance = req.result;
      resolve(dbInstance);
    };
    req.onerror = () => reject(req.error);
  });
}

/**
 * Load a Blob as an HTMLImageElement.
 * @param {Blob} blob
 * @returns {Promise<HTMLImageElement>}
 */
function loadBlobAsImage(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    img.src = url;
  });
}

/**
 * Create a trimmed preview blob by cropping transparent pixels from an image blob.
 * @param {Blob} blob
 * @returns {Promise<Blob>}
 */
async function createTrimmedPreview(blob) {
  const img = await loadBlobAsImage(blob);
  const w = img.naturalWidth;
  const h = img.naturalHeight;

  const canvas = new OffscreenCanvas(w, h);
  const ctx = /** @type {OffscreenCanvasRenderingContext2D} */ (canvas.getContext('2d'));
  ctx.drawImage(img, 0, 0);

  const data = ctx.getImageData(0, 0, w, h).data;

  // Find bounding box of non-transparent pixels
  let minX = w,
    minY = h,
    maxX = 0,
    maxY = 0;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const alpha = /** @type {number} */ (data[(y * w + x) * 4 + 3]);
      if (alpha > 0) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }

  // If fully transparent, return original
  if (maxX < minX) return blob;

  const cropW = maxX - minX + 1;
  const cropH = maxY - minY + 1;
  const trimmed = new OffscreenCanvas(cropW, cropH);
  const tCtx = /** @type {OffscreenCanvasRenderingContext2D} */ (trimmed.getContext('2d'));
  tCtx.drawImage(canvas, minX, minY, cropW, cropH, 0, 0, cropW, cropH);

  return trimmed.convertToBlob({ type: 'image/png' });
}

/**
 * Store an alternate asset blob and return its object URL.
 * Also generates and stores a trimmed preview for thumbnails.
 * @param {string} slug
 * @param {Blob} blob
 * @returns {Promise<AlternateEntry>}
 */
export async function saveAlternate(slug, blob) {
  const db = await openAlternatesDB();
  const previewBlob = await createTrimmedPreview(blob);
  const objectUrl = URL.createObjectURL(blob);
  const previewObjectUrl = URL.createObjectURL(previewBlob);
  /** @type {AlternateEntry} */
  const entry = { blob, objectUrl, previewBlob, previewObjectUrl, useAlternate: true };
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(entry, slug);
    tx.oncomplete = () => resolve(entry);
    tx.onerror = () => reject(tx.error);
  });
}

/** @param {string} slug */
export async function deleteAlternate(slug) {
  const db = await openAlternatesDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(slug);
    tx.oncomplete = () => resolve(undefined);
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * @param {string} slug
 * @param {boolean} enabled
 */
export async function setUseAlternate(slug, enabled) {
  const db = await openAlternatesDB();
  return new Promise((resolve, reject) => {
    const store = db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME);
    const req = store.get(slug);
    req.onsuccess = () => {
      const entry = req.result;
      if (!entry) {
        resolve(undefined);
        return;
      }
      entry.useAlternate = enabled;
      const putReq = store.put(entry, slug);
      putReq.onsuccess = () => resolve(undefined);
      putReq.onerror = () => reject(putReq.error);
    };
    req.onerror = () => reject(req.error);
  });
}

/** @returns {Promise<Map<string, AlternateEntry>>} */
export async function getAllAlternates() {
  const db = await openAlternatesDB();
  return new Promise((resolve, reject) => {
    const map = new Map();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).openCursor();
    req.onsuccess = () => {
      const cursor = req.result;
      if (cursor) {
        // Re-create object URLs from blobs (previous URLs are revoked between sessions)
        const entry = cursor.value;
        entry.objectUrl = URL.createObjectURL(entry.blob);
        entry.previewObjectUrl = URL.createObjectURL(entry.previewBlob);
        map.set(cursor.key, entry);
        cursor.continue();
      } else {
        resolve(map);
      }
    };
    req.onerror = () => reject(req.error);
  });
}
